import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatCentsToUsd } from '../../../helpers/formatCentsToUsd';
import { useSort } from '../../../hooks/useSort';
import SortableHeaderCell from '../../../components/atom/SortableHeaderCell';
import { getTransactionSourceLabel } from '../helpers/getTransactionSourceLabel';

function PayoutReportTable({ summary, excluded, transactions = [] }) {
    const { t } = useTranslation();
    const itemsWithLabels = transactions.map((row) => ({
        ...row,
        sourceLabel: getTransactionSourceLabel(
            {
                transactionSource: row.source,
                transactionAgentName: row.agentName,
                transactionCommissionGroups: row.commissionGroups,
            },
            t,
        ),
    }));
    const sort = useSort(itemsWithLabels, 'bookingId', 'asc');

    const summaryRows = [
        { key: 'totalAmount', value: summary.totalAmount },
        { key: 'processingFees', value: summary.processingFees },
        { key: 'bookingFeesTotal', value: summary.bookingFeesTotal },
        { key: 'bookingFeesVenueRebate', value: summary.bookingFeesVenueRebate },
        { key: 'bookingFeesFireknife', value: summary.bookingFeesFireknife },
    ];

    return (
        <Box sx={{ width: '100%' }}>
            <TableContainer sx={{ maxHeight: '65vh' }}>
                <Table stickyHeader sx={{ width: '100%', mb: 3, '& thead th': { top: 0 } }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('reports.payout.line')}</TableCell>
                            <TableCell align="right">{t('reports.payout.amount')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {summaryRows.map((row) => (
                            <TableRow key={row.key}>
                                <TableCell>{t(`reports.payout.lines.${row.key}`)}</TableCell>
                                <TableCell align="right">{formatCentsToUsd(row.value)}</TableCell>
                            </TableRow>
                        ))}
                        <TableRow sx={{ backgroundColor: 'action.selected' }}>
                            <TableCell>
                                <strong>{t('reports.payout.lines.netToVenue')}</strong>
                            </TableCell>
                            <TableCell align="right">
                                <strong>{formatCentsToUsd(summary.netToVenue)}</strong>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            {excluded.affiliateBookingCount > 0 && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, textAlign: 'left' }}
                >
                    {t('reports.payout.excludedAffiliateLabel', {
                        amount: formatCentsToUsd(excluded.affiliateRevenue),
                        count: excluded.affiliateBookingCount,
                    })}
                </Typography>
            )}

            <TableContainer sx={{ maxHeight: '65vh' }}>
                <Table stickyHeader sx={{ width: '100%', '& thead th': { top: 0 } }} size="small">
                    <TableHead>
                        <TableRow>
                            <SortableHeaderCell field="bookingId" sort={sort}>
                                {t('reports.payout.cols.bookingId')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="show" sort={sort}>
                                {t('reports.payout.cols.show')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="showDate" sort={sort}>
                                {t('reports.payout.cols.showDate')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="sourceLabel" sort={sort}>
                                {t('reports.payout.cols.source')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="paymentType" sort={sort}>
                                {t('reports.payout.cols.paymentType')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="amount" sort={sort} align="right">
                                {t('reports.payout.cols.amount')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="processingFee" sort={sort} align="right">
                                {t('reports.payout.cols.processingFee')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="bookingFee" sort={sort} align="right">
                                {t('reports.payout.cols.bookingFee')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="venueRebate" sort={sort} align="right">
                                {t('reports.payout.cols.venueRebate')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="fireknifeFee" sort={sort} align="right">
                                {t('reports.payout.cols.fireknifeFee')}
                            </SortableHeaderCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sort.sortedItems.map((row) => (
                            <TableRow key={row.transactionId ?? `${row.bookingId}-${row.amount}`}>
                                <TableCell>{row.bookingId}</TableCell>
                                <TableCell>{row.show}</TableCell>
                                <TableCell>{row.showDate}</TableCell>
                                <TableCell>{row.sourceLabel}</TableCell>
                                <TableCell>{row.paymentType}</TableCell>
                                <TableCell align="right">{formatCentsToUsd(row.amount)}</TableCell>
                                <TableCell align="right">
                                    {formatCentsToUsd(row.processingFee)}
                                </TableCell>
                                <TableCell align="right">
                                    {formatCentsToUsd(row.bookingFee)}
                                </TableCell>
                                <TableCell align="right">
                                    {formatCentsToUsd(row.venueRebate)}
                                </TableCell>
                                <TableCell align="right">
                                    {formatCentsToUsd(row.fireknifeFee)}
                                </TableCell>
                            </TableRow>
                        ))}
                        <TableRow sx={{ backgroundColor: 'action.hover' }}>
                            <TableCell>
                                <strong>{t('reports.summary.total')}</strong>
                            </TableCell>
                            <TableCell>
                                <strong>
                                    {t('reports.summary.bookings', { count: summary.bookingCount })}
                                </strong>
                            </TableCell>
                            <TableCell />
                            <TableCell />
                            <TableCell />
                            <TableCell align="right">
                                <strong>{formatCentsToUsd(summary.totalAmount)}</strong>
                            </TableCell>
                            <TableCell align="right">
                                <strong>{formatCentsToUsd(summary.processingFees)}</strong>
                            </TableCell>
                            <TableCell align="right">
                                <strong>{formatCentsToUsd(summary.bookingFeesTotal)}</strong>
                            </TableCell>
                            <TableCell align="right">
                                <strong>{formatCentsToUsd(summary.bookingFeesVenueRebate)}</strong>
                            </TableCell>
                            <TableCell align="right">
                                <strong>{formatCentsToUsd(summary.bookingFeesFireknife)}</strong>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

export default PayoutReportTable;
